import { END_POINTS } from '@config';
import { api } from '@core/baseApi';
import {
  HTTPResponse,
  MemberTransactionType,
  OrgAndBranchId,
  OrgBranchOrderIds
} from '@types';

const extendedBaseQuery = api.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentList: builder.query<
      HTTPResponse<any>,
      { OrgAndBranchId: OrgAndBranchId; queryString: string }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.GET_PAYMENT_LIST(
          reqBody.OrgAndBranchId
        )}${reqBody.queryString}`,
        method: 'GET'
      })
    }),
    markAsPaid: builder.mutation<
      HTTPResponse<any>,
      { OrgAndBranchId: OrgAndBranchId; body: any }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.MARK_AS_PAID(reqBody.OrgAndBranchId)}`,
        body: reqBody.body,
        method: 'POST'
      })
    }),
    sendPaymentReminder: builder.mutation<
      HTTPResponse<any>,
      { OrgAndBranchId: OrgAndBranchId; body: any }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.SEND_PAYMENT_REMINDER(
          reqBody.OrgAndBranchId
        )}`,
        body: reqBody.body,
        method: 'POST'
      })
    }),
    disablePaymentOrder: builder.mutation<
      HTTPResponse<any>,
      { params: OrgBranchOrderIds }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.DISABLE_PAYMENT_ORDER(reqBody.params)}`,
        method: 'PATCH'
      })
    }),
    getPaymentOrderHistory: builder.query<
      HTTPResponse<any>,
      { params: OrgBranchOrderIds }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.GET_PAYMENT_ORDER_HISTORY(
          reqBody.params
        )}`,
        method: 'GET'
      })
    }),
    downloadPaymentReceipt: builder.query<
      HTTPResponse<any>,
      { orderId: string; splitOrder: boolean }
    >({
      query: (reqBody) => {
        const { orderId, splitOrder } = reqBody;
        return {
          url: `${END_POINTS.TRANSACTIONS.DOWNLOAD_PAYMENT_RECEIPT(
            orderId
          )}?split-order=${splitOrder}`,
          method: 'GET'
        };
      }
    }),
    getPaymentDetailsById: builder.query<
      HTTPResponse<MemberTransactionType>,
      { params: OrgBranchOrderIds }
    >({
      query: (req) => ({
        url: `${END_POINTS.TRANSACTIONS.GET_PAYMENT_DETAILS_BY_ID(req.params)}`,
        method: 'GET'
      })
    }),
    enablePartialPayment: builder.mutation<
      HTTPResponse<any>,
      { params: OrgBranchOrderIds }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.ENABLE_PARTIAL_PAYMENT(
          reqBody.params
        )}`,
        method: 'PATCH'
      })
    }),
    closePayment: builder.mutation<
      HTTPResponse<any>,
      { params: OrgBranchOrderIds; body: any }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.CLOSE_PAYMENT(reqBody.params)}`,
        body: reqBody.body,
        method: 'PATCH'
      })
    }),
    applyDiscount: builder.mutation<
      HTTPResponse<any>,
      { params: OrgBranchOrderIds; body: any }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.APPLY_DISCOUNT(reqBody.params)}`,
        body: reqBody.body,
        method: 'PATCH'
      })
    }),
    updateDue: builder.query<
      HTTPResponse<any>,
      { params: OrgBranchOrderIds; discountAmount: string }
    >({
      query: (reqBody) => ({
        url: `${END_POINTS.TRANSACTIONS.UPDATE_DUE(reqBody.params)}?amount=${
          reqBody.discountAmount
        }`,
        method: 'GET'
      })
    })
  })
});

// eslint-disable-next-line import/prefer-default-export
export const {
  useLazyGetPaymentListQuery,
  useMarkAsPaidMutation,
  useSendPaymentReminderMutation,
  useDisablePaymentOrderMutation,
  useLazyGetPaymentOrderHistoryQuery,
  useLazyDownloadPaymentReceiptQuery,
  useLazyGetPaymentDetailsByIdQuery,
  useEnablePartialPaymentMutation,
  useClosePaymentMutation,
  useApplyDiscountMutation,
  useLazyUpdateDueQuery
} = extendedBaseQuery;
